<template>
  <v-container fluid v-resize="onResize">
    <v-system-bar>
      <v-breadcrumbs :items="breadscrumbs()">
        <template v-slot:divider>
          <v-icon>mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>
      <v-spacer></v-spacer>
      <BaseItemProperty :page-properties="editedItem.page_properties"></BaseItemProperty>
    </v-system-bar>
    <v-card :height="windowSize.y - subtractHeight">
      <v-progress-linear
          :active="loading"
          :indeterminate="loading"
          absolute
          top
      >
      </v-progress-linear>
      <v-card-title>
        <v-card class="px-3 mx-1">
          {{ $t('columns.customer_order_title') }}:
          <router-link
              v-if="!!editedItem.customer_order_uid"
              class="ml-3" link outlined
              :to="{name:'CustomerOrderItem', params: { itemUid: editedItem.customer_order_uid}}">
            {{ editedItem.customer_order_title }}
          </router-link>
        </v-card>
        <v-card class="px-3 mx-1">
          {{ $t('columns.product') }}:
          <router-link
              v-if="!!editedItem.variant_uid"
              class="ml-3" link outlined
              :to="{name:'ProductItem', params: { itemUid: editedItem.variant_uid}}">
            {{ editedItem.variant_title }}
          </router-link>
        </v-card>
        <v-card class="px-3 mx-1">
          {{ $t('columns.customer_title') }}:
          <router-link
              v-if="!!editedItem.customer_uid"
              class="ml-3" link outlined
              :to="{name:'CustomerItem', params: { itemUid: editedItem.customer_uid}}">
            {{ editedItem.customer_title }}
          </router-link>
        </v-card>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <v-row no-gutters>
          <v-col cols="auto" class="mx-1" v-if="!!editedItem && !!editedItem.calendar_date">
            <v-text-field
                v-model="editedItem.calendar_date.dt"
                class="no-spin mb-n5"
                :label="$t('columns.delivery_date')"
                type="date"
                dense
                outlined
                :filled="lockPage"
                :readonly="lockPage"
            ></v-text-field>
          </v-col>
          <v-col cols="auto" class="mx-1" v-if="editedItem.has_smd">
            <v-text-field
                :label="$t('columns.plan_smd')"
                v-model.number="editedItem.plan_smd"
                class="no-spin mb-n5"
                dense
                outlined
                :filled="lockPage"
                :readonly="lockPage"
            ></v-text-field>
          </v-col>
          <v-col cols="auto" class="mx-1" v-if="editedItem.has_tht">
            <v-text-field
                :label="$t('columns.plan_tht')"
                v-model.number="editedItem.plan_tht"
                class="no-spin mb-n5"
                dense
                outlined
                :filled="lockPage"
                :readonly="lockPage"
            ></v-text-field>
          </v-col>
          <v-col cols="auto" class="mx-1" v-if="editedItem.has_smd">
            <v-text-field
                :label="$t('columns.as_is_smd')"
                v-model.number="editedItem.as_is_smd"
                class="no-spin mb-n5"
                dense
                outlined
                filled
                readonly
            ></v-text-field>
          </v-col>
          <v-col cols="auto" class="mx-1" v-if="editedItem.has_tht">
            <v-text-field
                :label="$t('columns.as_is_tht')"
                v-model.number="editedItem.as_is_tht"
                class="no-spin mb-n5"
                dense
                outlined
                filled
                readonly
            ></v-text-field>
          </v-col>
          <v-col cols="auto" class="mx-1">
            <v-chip outlined v-if="!!editedItem.sn_smd_from && !!editedItem.sn_smd_to">
              {{ $t('columns.sn_smd') }}: {{ editedItem.sn_smd_from }} - {{ editedItem.sn_smd_to }}
            </v-chip>
          </v-col>
          <v-col cols="auto" class="mx-1">
            <v-chip outline v-if="!!editedItem.sn_tht_from && !!editedItem.sn_tht_to">
              {{ $t('columns.sn_tht') }}: {{ editedItem.sn_tht_from }} - {{ editedItem.sn_tht_to }}
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-row>
        <v-col cols="8">
          <v-card :height="windowSize.y - subtractHeight - 150" class="overflow-y-auto">
            <BaseCardList
                v-if="!!editedItem && editedItem.delivery_tasks && !!editedItem.page_properties"
                :item-list="editedItem.delivery_tasks"
                :common="editedItemCommon"
                :get-card-title-color="getCardColor"
                :default-item="defaultTaskItem"
                xl="12" lg="12" md="12" sm="12" xs="12"
                :enable-add-row="enableAddTask"
                :disable-item-edit="disableBatchRunItemEdit"
                :show-deleted="editedItem.page_properties.show_deleted"
            >
              <template v-slot:body="{item, index, common}">
                <v-row no-gutters dense class="flex-nowrap" v-if="!!common">
                  <v-col cols="3">
                    <v-text-field
                        :label="$t('columns.name')"
                        v-model="item.name"
                        outlined
                        dense
                        class="mt-1 mx-1 mb-n5"
                        :filled="lockPage"
                        :readonly="lockPage"
                        :error="!validateTask(item, index, 'name')"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-select
                        :label="$t('columns.status')"
                        v-model="item.status"
                        :items="taskStatusList"
                        item-text="text"
                        item-value="value"
                        outlined
                        dense
                        class="mt-1 mx-1 mb-n5"
                        :filled="lockPage"
                        :readonly="lockPage"
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="1">
                    <v-text-field
                        :label="$t('columns.max_effort')"
                        v-model.number="item.max_effort"
                        outlined
                        dense
                        class="no-spin mt-1 mx-1 mb-n5"
                        type="number"
                        :filled="lockPage"
                        :readonly="lockPage"
                        :error="!validateTask(item, index, 'max_effort')"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="1">
                    <v-text-field
                        :label="$t('columns.spend_effort')"
                        v-model.number="item.spend_effort"
                        outlined
                        dense
                        type="number"
                        class="no-spin mt-1 mx-1 mb-n5"
                        :filled="lockPage"
                        :readonly="lockPage"
                    >
                    </v-text-field>
                  </v-col>
                  <v-col cols="2">
                    <v-select
                        :label="$t('columns.task_type')"
                        v-model="item.task_type"
                        :items="editedItemCommon.task_type_dto_list"
                        item-text="name"
                        return-object
                        outlined
                        dense
                        class="mt-1 mx-1 mb-n5"
                        @change="setMachines(item)"
                        :filled="lockPage"
                        :readonly="lockPage"
                        :error="!validateTask(item, index, 'task_type')"
                    >
                    </v-select>
                  </v-col>
                  <v-col cols="3">
                    <v-select
                        v-if="!!item && !!item.task_type"
                        :label="$t('columns.machines')"
                        v-model="item.machines"
                        :items="item.task_type.machines"
                        item-text="name"
                        return-object
                        multiple
                        outlined
                        dense
                        chips
                        class="mt-1 mx-1 mb-n5"
                        :filled="lockPage"
                        :readonly="lockPage"
                    >
                    </v-select>
                  </v-col>
                </v-row>
              </template>
            </BaseCardList>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
    <v-toolbar height="48">
      <v-spacer></v-spacer>
      <v-btn
          text
          elevation="3"
          class="mx-1"
          :to="{name: 'CustomerOrderTable'}"
      >
        {{ $t('Cancel') }}
      </v-btn>
      <v-btn
          text
          elevation="3"
          class="mx-1"
          :loading="commandLoading"
          @click="onSave()"
          :disabled="!validateBatchRunAll()"
      >
        {{ $t('Save') }}
      </v-btn>
    </v-toolbar>
    <ConfirmDialog :confirm_request="confirm_request" :fetch-request="fetchRequest"></ConfirmDialog>
  </v-container>
</template>

<script>
import {mapGetters, mapActions} from 'vuex';
import {mapFields} from 'vuex-map-fields';
import ConfirmDialog from "@/components/Base/ConfirmDialog";
import BaseCardList from "@/components/Base/BaseCardList";
import {v4 as uuidv4} from "uuid";
import BaseItemProperty from "@/components/Base/BaseItemProperty";

export default {
  name: 'BatchRunItem',
  props: {
    subtractHeight: {type: [Number], default: 100},
    height: {type: [Number], default: window.innerHeight},
    itemUid: {default: null},
    dto_detail_model: {type: [String], default: 'DeliveryBrDetailQueryDTO'},
    dto_save_model: {type: [String], default: 'DeliveryBrSaveCommandDTO'},
  },

  components: {
    ConfirmDialog,
    BaseCardList,
    BaseItemProperty,
  },

  computed: {
    ...mapGetters('BatchRunTable', ['getColumns', 'getStatusCustomerOrder']),
    ...mapFields('BatchRunTable', ['errorMessage', 'options', 'totalRecords', 'options.page',
      'editedItem', 'itemList', 'editedItemCommon', 'confirm_request',
      'loading', 'commandLoading', 'selectedItemList', 'searchTermFile', 'searchTerm',
      'statusCustomerOrder']),

    lockPage() {
      return !(!!this.editedItem && !!this.editedItem.page_properties && !this.editedItem.page_properties.lock_page)
    },

    getTaskType() {
      let result = []
      if (!!this.editedItemCommon.task_type_dto_list)
        this.editedItemCommon.task_type_dto_list.forEach(row => {
          result.push({text: row.name, value: row})
        })
      return result
    },
    taskStatusList() {
      let result = []
      if (!!this.editedItemCommon && !!this.editedItemCommon.task_status_color_list)
        for (const row of this.editedItemCommon.task_status_color_list)
          result.push({text: this.$t('batch_run_status.' + row.status), value: row.status})
      return result
    },
  },

  methods: {
    ...mapActions('BatchRunTable', ['openWebSocket', 'fetchItemList', 'fetchItem',
      'fetchSaveEditedItem', 'fetchActionSelectedItem', 'fetchActionItem', 'fetchRequest',]),

    onSave() {
      this.fetchSaveEditedItem(this.dto_save_model)
    },

    onResize() {
      this.windowSize = {x: window.innerWidth, y: window.innerHeight};
    },

    breadscrumbs() {
      return [
        {text: 'Home', href: '/site/',},
        {text: 'BatchRuns', href: '/site/batch_run/batch_run/',},
        // {
        //   text: this.editedItem.calendar_date.dt,
        //   href: '/site/batch_run/delivery/batchrun/' + this.$route.params.itemUid,
        // },
      ]
    },
    getCardColor: function ({item, index}) {
      // console.log('getCardColor', item, index)
      if (!!item && !!item.task_type)
        return item.task_type.color
      return null
    },
    onEditItem(Item) {
    },
    onDeleteItem(Item) {
    },
    setMachines(item) {
      this.machines = []
      if (!!item && !!item.task_type)
        item.task_type.machines.forEach(row => {
          this.machines.push(row)
        })
    },
    disableBatchRunItemEdit(item) {
      return this.lockPage || (!!item && !this.validateTaskItem(item))
    },
    enableAddTask(newDeliveryItem) {
      return this.validateTaskItem(newDeliveryItem)
    },
    validateTask(item, index, column) {
      // console.log('validateTask', item, index, column)
      if (this.editedItem.delivery_tasks == null) return true
      if (this.lockPage) return true
      if (!item) return true
      if (item.deleted === true) return true
      if (column === 'name' && !item.name) return false
      if (column === 'max_effort' && !item.max_effort) return false
      if (column === 'task_type' && !item.task_type) return false
      return true
    },
    validateTaskItem(item) {
      for (let column of ['name', 'max_effort', 'task_type']) {
        let result = this.validateTask(item, 0, column)
        if (result === false)
          return false
      }
      return true
    },
    validateBatchRunAll() {
      if (!!this.editedItem.delivery_tasks)
        for (let item of this.editedItem.delivery_tasks) {
          if (this.validateTaskItem(item) === false)
            return false
        }
      return true
    },
  },

  async mounted() {
    console.log('mounted')
    await this.fetchItem({itemUid: this.itemUid, dto_detail_model: this.dto_detail_model,})
  },

  beforeDestroy() {
  },

  data() {
    return {
      windowSize: {x: 0, y: 0,},
      valid: true,
      defaultTaskItem: {
        deleted: false,
        uid: uuidv4(),
        status: 'todo',
        max_effort: null,
        spend_effort: null,
        task_type: null,
        machines: [],
      },
      machines: [],
    }
  }
}
</script>

<style scoped lang="scss">
.no-spin input[type='number'] {
  -moz-appearance: textfield;
  /*width: 50px;*/
}

</style>

<i18n src="..\CustomerOrder\locales.json">
</i18n>
